import { Component, Show } from "solid-js"

interface HelpModalProps {
    isOpen: boolean
    onClose: () => void
}

const HelpModal: Component<HelpModalProps> = (props) => {
    return (
        <Show when={props.isOpen}>
            <div class="fixed inset-0 z-[2000] flex items-center justify-center p-2">
                <div class="flex max-h-[85vh] w-full max-w-5xl flex-col rounded-lg bg-white px-3 shadow-xl md:max-h-[75vh]">
                    {/* Header */}
                    <div class="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                        <h2 class="text-2xl font-bold text-neutral-800">Hjälp</h2>
                        <button class="text-gray-400 transition-colors hover:text-neutral-600" onClick={props.onClose}>
                            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div class="overflow-y-auto px-6 py-4 text-neutral-800">
                        <div class="grid gap-12 md:grid-cols-2">
                            {/* Left Column */}
                            <div class="space-y-6 leading-9">
                                {/* Lägen */}

                                <section>
                                    <h3 class="mb-1.5 text-lg font-semibold text-neutral-800">Allmänt</h3>
                                    <div class="space-y-3 text-sm">
                                        <div>
                                            <p class="mt-1 text-neutral-600">
                                                Kartbaserat verktyg för att skapa och spela upp scenarier i Stockholm. Standardscenariot baseras på mordet på Olof Palme 1986. Öppnas i Simulera-läge – välj Nytt scenario för att redigera.
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h3 class="mb-1.5 text-lg font-semibold text-neutral-800">Lägen</h3>
                                   
                                    <div class="space-y-3 text-sm">
                                        <div>
                                            <strong class="font-bold text-neutral-700">Simulera</strong>
                                            <p class="mt-1 text-neutral-600">Spela upp scenariot i realtid.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Redigera</strong>
                                            <p class="mt-1 text-neutral-600">Skapa/ändra aktörer, rutter, händelser och vittnesmål.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Dela</strong>
                                            <p class="mt-1 text-neutral-600">Dela scenariot via länk eller spara till disk.</p>
                                        </div>
                                    </div>
                                </section>

                                {/* Begrepp */}
                                <section>
                                    <h3 class="mb-1.5 text-lg font-semibold text-neutral-800">Begrepp</h3>
                                    <div class="space-y-3 text-sm">
                                        <div>
                                            <strong class="font-bold text-neutral-700">Aktörer</strong>
                                            <p class="mt-1 text-neutral-600">Personer, fordon eller enheter i scenariot (t.ex. polisbilar, ambulanser).</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Vägpunkter</strong>
                                            <p class="mt-1 text-neutral-600">Kartpunkter som definierar aktörens rutt. Läggs till genom klick.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Segment</strong>
                                            <p class="mt-1 text-neutral-600">Sammanhängande del av rutt. Använd vid uppehåll mellan förflyttningar.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Händelser</strong>
                                            <p class="mt-1 text-neutral-600">Tidsatta markeringar som visas vid angiven tidpunkt.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Vittnesmål</strong>
                                            <p class="mt-1 text-neutral-600">Beskrivningar av vad aktören upplevde/observerade.</p>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            {/* Right Column */}
                            <div class="space-y-6">
                                {/* Redigera rutter */}
                                <section>
                                    <h3 class="mb-1.5 text-lg font-semibold text-neutral-800">Redigera rutter</h3>
                                    <div class="space-y-3 text-sm">
                                        <div>
                                            <strong class="font-bold text-neutral-700">Lägg till</strong>
                                            <p class="mt-1 text-neutral-600">Välj "Lägg till vägpunkt" och klicka på kartan.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Flytta</strong>
                                            <p class="mt-1 text-neutral-600">Klicka på punkt, håll Shift och dra.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Start- och sluttider</strong>
                                            <p class="mt-1 text-neutral-600">Ange starttid eller ankomsttid. Kombinera med segment vid behov.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Hastiheter (riktvärden)</strong>
                                            <p class="mt-1 text-neutral-600">
                                                Person: 1,2–1,4 m/s (gång), 2,5–3,5 m/s (jogging), 5–7 m/s (sprint)
                                            </p>
                                            <p class="mt-1.5 text-neutral-600">
                                                Bil: ~6 m/s (≈22 km/h, lugn körning), ~12–14 m/s (≈43–50 km/h,
                                                stadstrafik), 18–25 m/s (≈65–90 km/h, utryckning)
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                {/* Scenarier */}
                                <section>
                                    <h3 class="mb-1.5 text-lg font-semibold text-neutral-800">Scenarier</h3>
                                    <div class="space-y-3 text-sm">
                                        <div>
                                            <strong class="font-bold text-neutral-700">Nytt scenario</strong>
                                            <p class="mt-1 text-neutral-600">Skapa från aktuell vy, tom mall eller importera JSON-fil.</p>
                                        </div>
                                        <div>
                                            <strong class="font-bold text-neutral-700">Delning</strong>
                                            <p class="mt-1 text-neutral-600">
                                                Dela scenariot via publik länk eller spara som JSON-fil till disk. Vid länkdelning kan andra spela upp eller kopiera scenariot, men inte ändra ditt original. Uppdaterade versioner kräver ny länk.
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h3 class="mb-1.5 text-lg font-semibold text-neutral-800">Kontakt</h3>
                                    <div class="space-y-3 text-sm">
                                        <div>
                                            <p class="mt-1 text-neutral-600">
                                                Frågor/feedback: <a href="mailto:support@sim86.se" class="text-blue-600 hover:underline">support@sim86.se</a>
                                            </p>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div class="flex justify-end border-t border-gray-200 px-6 py-4">
                        <button
                            class="rounded bg-gray-800 px-2.5 py-1 text-sm text-white transition-colors hover:bg-gray-700"
                            onClick={props.onClose}
                        >
                            Stäng
                        </button>
                    </div>
                </div>
            </div>
        </Show>
    )
}

export default HelpModal
